import React, { FC, useRef, useEffect, useState } from 'react';
import { colors } from 'constants/colors';
import { URL } from 'constants/urlMaps';
import { IProductsGroup } from 'Modules/Shops/types';
import useRootUrlWithSubdomain from 'utils/hooks/useRootUrlWithSubdomain';
import { getYear } from 'utils/date';
import { Flex } from '@igooods/ui-kit';
import BlockContainer from 'Components/landings/BlockContainer';
import ExternalLink from 'Components/ExternalLink';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getCurrentCity } from 'Modules/SearchAddress/selectors';
import { phoneFormat } from 'utils/helpers';
import { Grid } from '@igooods/ui-kit';
import { Box } from '@igooods/ui-kit';
import { mediaMdDown, mediaSmDown, mediaLgDown, mediaCustomDown } from 'theme/helpers/css';
import { useStickySupportButton } from 'utils/hooks';
import { isBrowser } from 'utils/helpers';
import useScrollDirection from 'utils/hooks/useScrollDirection';

export const FOOTER_CONTACTS_ID = 'FOOTER_CONTACTS_ID';

const LinksGrid = styled(Grid)`
  grid-template-areas:
    'nav contacts'
    'seo contacts';
  grid-template-columns: 1fr 180px;

  ${mediaMdDown`
    grid-template-areas:
      'nav contacts'
      'seo seo';
      grid-template-columns: 0.6666fr 0.3333fr;
  `}

  ${mediaSmDown`
    grid-template-areas:
      'contacts'
      'nav'
      'seo';
      grid-template-columns: 1fr;
  `}
`;

const Nav = styled(Flex).attrs({ wrap: true })`
  grid-area: nav;

  ${mediaMdDown`
    margin-bottom: -18px;
  `}

  ${mediaSmDown`
    margin-right: -28px;
  `}
`;

const NavLink = styled(ExternalLink)`
  width: 20%;
  padding-right: 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;

  ${mediaMdDown`
    width: 50%;
    margin-bottom: 18px;
  `}

  ${mediaSmDown`
    padding-right: 14px;
    margin-bottom: 12px;
  `}
`;

const ContactsContainer = styled.div`
  grid-area: contacts;
  margin-right: 72px;

  ${mediaCustomDown(1441)`margin-right: 0;`}

  ${mediaLgDown`
  margin-right: 72px;
`}
`;

const Contacts = styled(Flex).attrs({
  column: true,
  bottom: true,
})`
  ${mediaSmDown`
    align-items: flex-start;
  `}
`;

const Phone = styled(ExternalLink)`
  font-size: 14px;
  line-height: 18px;
  white-space: nowrap;
  font-weight: 600;
`;

const Time = styled.div`
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
`;

const Seo = styled(Flex)`
  grid-area: seo;
  margin-bottom: -8px;
`;

const Copyright = styled(Box).attrs({
  mb: 2,
  pr: 4,
})`
  font-size: 10px;
  line-height: 16px;
  color: ${colors.warmGrey};

  width: 20%;

  ${mediaSmDown`
    width: 100%;
  `}
`;

const getNavMap = (mainRoute: string) => {
  return [
    {
      text: 'Доставка и оплата',
      url: `${mainRoute}${URL.payment}`,
    },
    {
      text: 'Для бизнеса',
      url: `${mainRoute}${URL.business}?ref=site_new`,
    },
    {
      text: 'Франшиза',
      url: URL.franchisee,
    },
    {
      text: 'О компании',
      url: `${mainRoute}${URL.how_it_works}`,
    },
    {
      text: 'Работа в igooods',
      url: `https://rabota.igooods.ru`,
    },
  ];
};

interface ISeoLinks {
  groups?: IProductsGroup[];
}

const SeoLinks: FC<ISeoLinks> = () => {
  const { mainRoute } = useRootUrlWithSubdomain();
  const currentYear = getYear();
  const currentCity = useSelector(getCurrentCity);
  const contactsRef = useRef<HTMLDivElement>(null);
  const { scrollTop } = useScrollDirection(isBrowser ? window.root : null);
  const [isShowUsedeskBtn, setIsShowUsedeskBtn] = useState(false);

  useEffect(() => {
    window.usedeskMessenger?.toggle(false);
  }, []);

  useEffect(() => {
    if (scrollTop > 300 && !isShowUsedeskBtn) {
      window.usedeskMessenger?.toggle(true);
      setIsShowUsedeskBtn(true);
    }
  }, [isShowUsedeskBtn, scrollTop]);

  const navMap = getNavMap(mainRoute);

  useStickySupportButton(isBrowser ? window.root : null, contactsRef.current);

  const copyrightText = `© igooods 2014—${currentYear}`;

  return (
    <BlockContainer bgColor={colors.white}>
      <Box pt={{ xs: 2.5, sm: 5.5, md: 4.5, lg: 6 }} pb={{ xs: 15, sm: 14, md: 15, lg: 22 }}>
        <LinksGrid gapCol={{ xs: 0, md: 8 }} gapRow={{ xs: 6, md: 1.4 }}>
          <ContactsContainer ref={contactsRef}>
            <Contacts id={FOOTER_CONTACTS_ID}>
              {currentCity?.phone && <Phone href={`tel:${currentCity.phone}`}>{phoneFormat(currentCity.phone)}</Phone>}
              <Time>10:00-22:00</Time>
            </Contacts>
          </ContactsContainer>
          <Nav>
            {navMap.map(({ text, url }) => (
              <NavLink key={text} href={url}>
                {text}
              </NavLink>
            ))}
          </Nav>
          <Seo>
            <Copyright>{copyrightText}</Copyright>
          </Seo>
        </LinksGrid>
      </Box>
    </BlockContainer>
  );
};

export default SeoLinks;
